import { AdhanService } from './AdhanService';

/**
 * Generate different year ranges for Gregorian and Hijri calendars
 * For Hijri, we need to generate years in the Hijri calendar (like 1440-1450)
 */
export var generateYears = function generateYears(calendarType, yearsFrom, yearsTo, isYearValidFn) {
  var currentYear = new Date().getFullYear();
  var gregStartYear = currentYear + yearsFrom;
  var gregEndYear = currentYear + yearsTo;
  var years = [];
  if (calendarType === 'G') {
    // For Gregorian calendar, use the props directly
    years = Array.from({
      length: Math.abs(gregEndYear - gregStartYear) + 1
    }, function (_, i) {
      return gregStartYear < gregEndYear ? gregStartYear + i : gregStartYear - i;
    });
  } else {
    // For Hijri calendar, convert the range to Hijri years
    // Get an approximation of current Hijri year
    var today = new Date();
    var currentHijriDate = AdhanService.GregorianToHijri(today);
    var currentHijriYear = (currentHijriDate === null || currentHijriDate === void 0 ? void 0 : currentHijriDate.year) || 1445; // Fallback to 1445 if conversion fails

    // Adjust the range proportionally (Hijri years are shorter)
    // The ratio is approximately 33 Hijri years per 32 Gregorian years
    var hijriYearsFrom = Math.round(yearsFrom * (33 / 32));
    var hijriYearsTo = Math.round(yearsTo * (33 / 32));
    var hijriStartYear = currentHijriYear + hijriYearsFrom;
    var hijriEndYear = currentHijriYear + hijriYearsTo;
    years = Array.from({
      length: Math.abs(hijriEndYear - hijriStartYear) + 1
    }, function (_, i) {
      return hijriStartYear < hijriEndYear ? hijriStartYear + i : hijriStartYear - i;
    });
  }

  // Filter years based on min/max date constraints
  return years.filter(isYearValidFn);
};

/**
 * Get days in month, accounting for leap years
 */
export var getDaysInMonth = function getDaysInMonth(month, year) {
  var isHijri = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  if (isHijri) {
    // Hijri months have either 29 or 30 days
    return month === 11 ? 30 : month % 2 === 0 ? 30 : 29;
  }
  if (month === 1) {
    // February
    return isLeapYear(year) ? 29 : 28;
  }
  return [3, 5, 8, 10].includes(month) ? 30 : 31;
};

/**
 * Check for leap year
 */
export var isLeapYear = function isLeapYear(year) {
  return year % 4 === 0 && year % 100 !== 0 || year % 400 === 0;
};

// Convert Gregorian date to Hijri date
export var gregorianToHijri = function gregorianToHijri(gregorianDate) {
  if (!gregorianDate) return null;
  try {
    var date = new Date(gregorianDate);
    if (isNaN(date.getTime())) return null;
    return AdhanService.GregorianToHijri(date);
  } catch (error) {
    console.error('Error converting to Hijri:', error);
    return null;
  }
};

// Convert Hijri date to Gregorian date
export var hijriToGregorian = function hijriToGregorian(hijriDay, hijriMonth, hijriYear) {
  if (hijriDay === null || hijriMonth === null || hijriYear === null) return null;
  try {
    return AdhanService.HijriToGregorian(hijriYear, hijriMonth, hijriDay);
  } catch (error) {
    console.error('Error converting to Gregorian:', error);
    return null;
  }
};

// Get valid months based on selected year
export var getValidMonths = function getValidMonths(_ref, selectedYear) {
  var calendarType = _ref.calendarType,
    minValidDate = _ref.minValidDate,
    maxValidDate = _ref.maxValidDate;
  if (selectedYear === null) return [];
  var validMonths = Array.from({
    length: 12
  }, function (_, i) {
    return i;
  });
  if (minValidDate && calendarType === 'G') {
    if (selectedYear === minValidDate.getFullYear()) {
      // Filter out months before min date
      validMonths = validMonths.filter(function (m) {
        return m >= minValidDate.getMonth();
      });
    }
  }
  if (maxValidDate && calendarType === 'G') {
    if (selectedYear === maxValidDate.getFullYear()) {
      // Filter out months after max date
      validMonths = validMonths.filter(function (m) {
        return m <= maxValidDate.getMonth();
      });
    }
  }
  if (calendarType === 'H' && (minValidDate || maxValidDate)) {
    // For Hijri, we need to convert min/max dates to Hijri first
    if (minValidDate) {
      var minHijri = gregorianToHijri(minValidDate);
      if (minHijri && selectedYear === minHijri.year) {
        validMonths = validMonths.filter(function (m) {
          return m >= minHijri.month;
        });
      }
    }
    if (maxValidDate) {
      var maxHijri = gregorianToHijri(maxValidDate);
      if (maxHijri && selectedYear === maxHijri.year) {
        validMonths = validMonths.filter(function (m) {
          return m <= maxHijri.month;
        });
      }
    }
  }
  return validMonths;
};

// Get valid days based on selected year and month
export var getValidDays = function getValidDays(_ref2, selectedYear, selectedMonth) {
  var calendarType = _ref2.calendarType,
    minValidDate = _ref2.minValidDate,
    maxValidDate = _ref2.maxValidDate;
  if (selectedYear === null || selectedMonth === null) return [];
  var isHijri = calendarType === 'H';
  var daysInMonth = getDaysInMonth(selectedMonth, selectedYear, isHijri);
  var validDays = Array.from({
    length: daysInMonth
  }, function (_, i) {
    return i + 1;
  });
  if (minValidDate && calendarType === 'G') {
    if (selectedYear === minValidDate.getFullYear() && selectedMonth === minValidDate.getMonth()) {
      // Filter out days before min date
      validDays = validDays.filter(function (d) {
        return d >= minValidDate.getDate();
      });
    }
  }
  if (maxValidDate && calendarType === 'G') {
    if (selectedYear === maxValidDate.getFullYear() && selectedMonth === maxValidDate.getMonth()) {
      // Filter out days after max date
      validDays = validDays.filter(function (d) {
        return d <= maxValidDate.getDate();
      });
    }
  }
  if (calendarType === 'H' && (minValidDate || maxValidDate)) {
    // For Hijri, we need to convert min/max dates to Hijri first
    if (minValidDate) {
      var minHijri = gregorianToHijri(minValidDate);
      if (minHijri && selectedYear === minHijri.year && selectedMonth === minHijri.month) {
        validDays = validDays.filter(function (d) {
          return d >= minHijri.day;
        });
      }
    }
    if (maxValidDate) {
      var maxHijri = gregorianToHijri(maxValidDate);
      if (maxHijri && selectedYear === maxHijri.year && selectedMonth === maxHijri.month) {
        validDays = validDays.filter(function (d) {
          return d <= maxHijri.day;
        });
      }
    }
  }
  return validDays;
};

// Determine if a year is valid
export var isYearValid = function isYearValid(_ref3) {
  var calendarType = _ref3.calendarType,
    minValidDate = _ref3.minValidDate,
    maxValidDate = _ref3.maxValidDate;
  return function (y) {
    if (calendarType === 'G') {
      if (minValidDate && y < minValidDate.getFullYear()) return false;
      if (maxValidDate && y > maxValidDate.getFullYear()) return false;
    } else {
      // For Hijri calendar
      if (minValidDate) {
        var minHijri = gregorianToHijri(minValidDate);
        if (minHijri && y < minHijri.year) return false;
      }
      if (maxValidDate) {
        var maxHijri = gregorianToHijri(maxValidDate);
        if (maxHijri && y > maxHijri.year) return false;
      }
    }
    return true;
  };
};
export var fromDateStrToObject = function fromDateStrToObject(dateStr, type) {
  var _dateStr;
  if (dateStr && dateStr.indexOf('-') === -1) return null;
  if (type === 'H') {
    var hijriDate = gregorianToHijri(new Date(dateStr));
    if (!hijriDate) return null;
    dateStr = "".concat(hijriDate.year, "-").concat(hijriDate.month, "-").concat(hijriDate.day || "1");
  }
  var parts = (_dateStr = dateStr) === null || _dateStr === void 0 ? void 0 : _dateStr.split('-').map(Number);
  if (parts && parts.length === 3) {
    return {
      day: parts[2],
      month: parts[1],
      year: parts[0]
    };
  }
};
export var isHijriYear = function isHijriYear(year) {
  return year < 1600;
};